import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MonitoredIdentityDto, UpdateMonitoredIdentityDto, IdType } from '../services';
import { formatNationalId } from '../utils/nationalIdFormatter';
import DisplayName from './DisplayName';

interface EditMonitoredIdentityFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateMonitoredIdentityDto) => Promise<void>;
  identity: MonitoredIdentityDto | null;
}

const EditMonitoredIdentityForm: React.FC<EditMonitoredIdentityFormProps> = ({
  open,
  onClose,
  onSubmit,
  identity
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{fullName?: string}>({});
  const [formData, setFormData] = useState<UpdateMonitoredIdentityDto>({
    fullName: '',
    isActive: true
  });

  useEffect(() => {
    if (identity) {
      setFormData({
        fullName: identity.fullName || '',
        isActive: identity.isActive
      });
    }
  }, [identity]);

  const handleClose = () => {
    if (isSubmitting) return;
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    if (!identity) return false;

    const newErrors: {fullName?: string} = {};

    // For company IDs, validate full name
    if (identity.nationalId?.idType === IdType.CompanyId) {
      if (!formData.fullName?.trim()) {
        newErrors.fullName = t('validation.companyNameRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UpdateMonitoredIdentityDto, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const formatIdType = (idType: IdType) => {
    if (idType === IdType.PersonalId) return t('registration.personalId');
    if (idType === IdType.CompanyId) return t('registration.companyId');
    if (idType === IdType.DIMEX) return t('idType.dimex');
    return t('registration.personalId'); // fallback
  };

  if (!identity) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {t('identities.editTitle')}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Identity Information Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('identities.nationalId')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {identity.nationalId ? formatNationalId(
                  identity.nationalId.normalizedId || '', 
                  identity.nationalId.idType || IdType.PersonalId
                ) : '--'}
              </Typography>
              <Chip
                label={formatIdType(identity.nationalId?.idType)}
                size="small"
                color={identity.nationalId?.idType === IdType.PersonalId ? 'primary' : 'secondary'}
              />
            </Box>

            <Divider />

            {/* Name Information Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {identity.nationalId?.idType === IdType.PersonalId ? t('identities.firstName') : t('identities.companyName')}
              </Typography>
              
              {identity.nationalId?.idType === IdType.PersonalId ? (
                // Personal ID - show resolved name with status indicators
                <Box>
                  <DisplayName identity={identity} showIdType={false} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('identities.personalNameReadOnly')}
                  </Typography>
                </Box>
              ) : (
                // Company ID - editable name field
                <TextField
                  fullWidth
                  required
                  label={t('identities.companyName')}
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  disabled={isSubmitting}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                />
              )}
            </Box>

            <Divider />

            {/* Status Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('identities.isActive')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={formData.isActive ? t('identities.isActive') : t('identities.inactive')}
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('messages.saving')}
              </>
            ) : (
              t('identities.update')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditMonitoredIdentityForm;